import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';

const Role = observer(() => {
	const { assignUsersStore } = useStores();
	const { roles, properties, status } = assignUsersStore;
	const tenantId = useAssignmentTenantId();

	const {
		draft: {
			customSetup: { selectedRoles, selectedRolePropertyIds }
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');

	// Roles are directory-wide, so only the property filter follows the tenant.
	useEffect(() => {
		assignUsersStore.fetchRoles();
	}, []);

	useEffect(() => {
		if (
			!properties.length ||
			assignUsersStore.propertiesTenantId !== tenantId
		) {
			assignUsersStore.fetchProperties(tenantId);
		}
	}, [tenantId]);

	const propertyOptions = properties.map((p) => ({
		id: p.id,
		label: p.name
	}));

	const filtered = search.trim()
		? roles.filter((r) =>
				r.name.toLowerCase().includes(search.toLowerCase())
		  )
		: roles;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const role = roles.find((r) => r.id === numId);
		if (!role) return;
		if (checked !== selectedRoles.some((r) => r.id === numId)) {
			toggleAssignUsersSelection('selectedRoles', {
				id: role.id,
				name: role.name
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedRoles.length === filtered.length) {
			setAssignUsersSelection('selectedRoles', []);
		} else {
			setAssignUsersSelection(
				'selectedRoles',
				filtered.map((r) => ({ id: r.id, name: r.name }))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((r) => selectedRoles.some((sr) => sr.id === r.id));
	const isLoading = status.fetchRoles === Status.Pending;

	return (
		<div id="lms-ca-assign-users-role">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div id="lms-ca-au-role-property-filter" className={styles.search}>
						<div className={styles.selectProp}>Select Property</div>
						<MultiSelectDropdown
							options={propertyOptions}
							placeholder="Select option"
							onChange={(ids) =>
								setAssignUsersPropertyFilter(
									'selectedRolePropertyIds',
									ids
								)
							}
							defaultSelectedIds={selectedRolePropertyIds}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-role-search"
						className={styles.search}
						placeholder="Search"
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title="Select role"
					astrix={true}
				/>
				{filtered.length > 1 && (
					<div
						id="lms-ca-au-role-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isAllSelected ? 'Deselect all' : 'Select all'}
					</div>
				)}
			</div>
			<div id="lms-ca-au-role-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{filtered.map((role) => {
					const isChecked = selectedRoles.some((r) => r.id === role.id);
					return (
						<CheckBoxCard
							key={role.id}
							id={role.id}
							htmlId={`lms-ca-au-role-${role.id}`}
							icon={
								<Icon
									type={
										isChecked ? 'roleOptFilled' : 'roleOptIcon'
									}
								/>
							}
							title={role.name}
							desc=""
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Role;
